import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { PaymentProviders as PaymentProviderIDs } from "global/enums";
import { PropsWithChildren, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectLoading, selectPaymentProviders } from "./PaymentProvidersSlice";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faToggleOff, faToggleOn, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { checkUserHasPermission } from "services/userSession/UserPermittedSlice";
import translations from "utils/translationUtility";

export type PaymentProviderSettingsProps = {
  allowEditing: boolean;
};

type PaymentProviderProps = {
  paymentProviderDisplayName: string;
  paymentProviderID: PaymentProviderIDs;
  setPaymentProviderEnabled: (enable: boolean) => void;
  removePaymentProvider?: () => void;
  PaymentProviderSettings: React.FC<PaymentProviderSettingsProps>;
  PaymentProviderSetup: React.FC;
};

const PaymentProvider: React.FC<PaymentProviderProps> = ({
  paymentProviderDisplayName,
  paymentProviderID,
  setPaymentProviderEnabled,
  removePaymentProvider,
  PaymentProviderSettings,
  PaymentProviderSetup,
}: PropsWithChildren<PaymentProviderProps>) => {
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const paymentProviders = useSelector(selectPaymentProviders);
  const loading = useSelector(selectLoading);

  const paymentProviderModel = useMemo(() => {
    if (paymentProviders.length > 0) {
      return paymentProviders.find((x) => x.paymentProviderID === paymentProviderID);
    }
  }, [paymentProviderID, paymentProviders]);

  const handleCloseEnableModal = (answer: boolean) => {
    setShowEnableModal(false);
    if (answer) {
      setPaymentProviderEnabled(!paymentProviderModel?.enabled);
    }
  };

  const handleCloseRemoveModal = (answer: boolean) => {
    setShowRemoveModal(false);
    if (answer && removePaymentProvider) {
      removePaymentProvider();
    }
  };

  const paymentProviderReady = paymentProviderModel !== undefined && paymentProviderModel.setupComplete;
  const userCanEditPredicate = (permission: Permissions) => permission === Permissions.EditPaymentSettings;

  return (
    <ActivityIndicator isBusy={loading}>
      <div className="card d-inline-flex">
        <div className="card-header">
          <div className="card-title">{paymentProviderDisplayName} Settings</div>
          {paymentProviderReady && (
            <Permission permissionPredicate={userCanEditPredicate}>
              <YesNoAlert
                showAlert={showEnableModal}
                alertTitle={`${paymentProviderModel.enabled ? "Disable" : "Enable"} ${paymentProviderDisplayName}`}
                alertText={
                  paymentProviderModel?.enabled
                    ? `Are you sure you want to disable ${paymentProviderDisplayName}? ${translations.client} Portal payments will be disabled until a new payment provider is enabled.`
                    : `Are you sure you want to enable ${paymentProviderDisplayName}? This will disable the current payment provider.`
                }
                callBack={handleCloseEnableModal}
                style={Style.Warning}
              />
              <YesNoAlert
                showAlert={showRemoveModal}
                alertTitle={`Remove ${paymentProviderDisplayName}`}
                alertText={`Are you sure you want to remove ${paymentProviderDisplayName}? This will delete all stored credentials for this provider.`}
                callBack={handleCloseRemoveModal}
                style={Style.Warning}
              />
              <div className="card-toolbar">
                {removePaymentProvider && (
                  <button
                    data-test-id="payment-provider-tab__remove-payment-provider-button"
                    type="button"
                    className="btn btn-icon btn-danger me-2"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Remove payment provider"
                    onClick={() => setShowRemoveModal(true)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
                <button
                  data-test-id="payment-provider-tab__toggle-enabled-button"
                  type="button"
                  className={"btn btn-icon" + (paymentProviderModel.enabled ? " btn-success" : " btn-danger")}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={paymentProviderModel.enabled ? "Enabled" : "Disabled"}
                  onClick={() => setShowEnableModal(true)}
                >
                  <FontAwesomeIcon icon={paymentProviderModel.enabled ? faToggleOn : faToggleOff} />
                </button>
              </div>
            </Permission>
          )}
        </div>
        <div className="card-body">
          {paymentProviderReady ? (
            <PaymentProviderSettings allowEditing={checkUserHasPermission(userCanEditPredicate)} />
          ) : (
            <Permission permissionPredicate={userCanEditPredicate}>
              <PaymentProviderSetup />
            </Permission>
          )}
        </div>
      </div>
    </ActivityIndicator>
  );
};

export default PaymentProvider;
